import { useQuery } from '@tanstack/react-query';
import type {
  CreateUserInteractionResponseDto,
  UserInteractionActionTypeType,
  UserInteractionListResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export interface UserInteractionFilters {
  actionType: UserInteractionActionTypeType;
  specificRecordId?: number;
  limit?: number;
}

export const useUserInteractionsApi = (api: AxiosInstance) => {
  const baseUrl = `/user-interactions`;

  const createInteraction = async (params: {
    actionType: UserInteractionActionTypeType;
    specificRecordId?: number;
  }): Promise<CreateUserInteractionResponseDto> => {
    const { data } = await api.post(baseUrl, params);
    return data;
  };

  const fetchUserInteractions = async (
    filters: UserInteractionFilters,
  ): Promise<UserInteractionListResponseDto> => {
    const { data } = await api.get(baseUrl, { params: filters });
    return data;
  };

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createInteraction,
    });

  const getQueryKey = (filters: UserInteractionFilters) => [baseUrl, filters] as const;

  const useUserInteractions = (filters: UserInteractionFilters, options?: { enabled?: boolean }) =>
    useQuery({
      queryKey: getQueryKey(filters),
      queryFn: () => fetchUserInteractions(filters),
      enabled: options?.enabled ?? true,
    });

  return {
    useCreate,
    useUserInteractions,
    getQueryKey,
  };
};
